import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react'
import { Fragment, forwardRef } from 'react'

import { SlotProvider } from '@adverity/ds-core/slot'
import { Tooltip } from '@adverity/ds-core/tooltip'
import { cn } from '@adverity/ds-tokens/cn'
import { Slot, Slottable } from '@radix-ui/react-slot'

import { NESTED_INTERACTIVE_CLASSES } from '../nested-interactive-classes'
import type { ButtonColor, ButtonSize, ButtonVariant } from './button-variants'
import { buttonVariants } from './button-variants'

const textButtonIconSizeMap = { small: 'x-small', medium: 'x-small', large: 'small' } as const
const iconOnlyIconSizeMap = { small: 'x-small', medium: 'small', large: 'small' } as const

type ButtonBaseProps = Omit<ComponentPropsWithoutRef<'button'>, 'color' | 'prefix' | 'style'> & {
    variant?: ButtonVariant
    color?: ButtonColor
    size?: ButtonSize
    disabled?: boolean
    isLoading?: boolean
    fullWidth?: boolean
    asChild?: boolean
}

type TextButtonCommonProps = ButtonBaseProps & {
    prefix?: ReactNode
    suffix?: ReactNode
    tooltip?: ReactNode
    icon?: never
    rounded?: never
}

type TextButtonAsChildProps = TextButtonCommonProps & {
    asChild: true
    children: ReactElement
    label?: string
}

type TextButtonStringChildrenProps = TextButtonCommonProps & {
    asChild?: false | undefined
    children: string
    label?: string
}

type TextButtonNonStringChildrenProps = TextButtonCommonProps & {
    asChild?: false | undefined
    children: Exclude<ReactNode, string>
    label: string
}

type TextButtonProps = TextButtonAsChildProps | TextButtonStringChildrenProps | TextButtonNonStringChildrenProps

type IconButtonProps = ButtonBaseProps & {
    icon: ReactNode
    label: string
    rounded?: boolean
    prefix?: never
    suffix?: never
} & ({ asChild: true; children: ReactElement } | { asChild?: false | undefined; children?: never })

type ButtonProps = TextButtonProps | IconButtonProps

const LoadingSpinner = ({ className }: { className?: string }) => (
    <svg
        className={cn('animate-spin', className)}
        width="1em"
        height="1em"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
    >
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
    </svg>
)

const isIconOnlyProps = (props: ButtonProps): props is IconButtonProps => 'icon' in props && props.icon !== undefined

// oxlint-disable-next-line complexity
const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
    const iconOnly = isIconOnlyProps(props)
    const { variant, color, size, disabled, isLoading, fullWidth, asChild, className, ...rest } = props
    const Comp = asChild ? Slot : 'button'

    const classes = cn(
        buttonVariants({ variant, color, size, iconOnly }),
        NESTED_INTERACTIVE_CLASSES,
        fullWidth && 'w-full',
        disabled &&
            !isLoading &&
            (variant === 'minimal'
                ? 'pointer-events-none cursor-not-allowed bg-transparent text-background-emphasis'
                : 'pointer-events-none cursor-not-allowed border-background-emphasis bg-background-emphasis text-text-muted'),
        isLoading && 'pointer-events-none cursor-wait',
        className,
    )

    if (iconOnly) {
        const {
            icon,
            label,
            rounded,
            children: iconChildren,
            ...iconRest
        } = rest as Omit<IconButtonProps, 'variant' | 'color' | 'size' | 'disabled' | 'isLoading' | 'asChild'>

        return (
            <SlotProvider slots={{ icon: { size: iconOnlyIconSizeMap[size ?? 'medium'] } }}>
                <Tooltip content={label}>
                    <Comp
                        ref={ref}
                        className={cn(classes, rounded && 'rounded-round')}
                        aria-label={label}
                        disabled={asChild ? undefined : disabled || isLoading}
                        aria-disabled={disabled || isLoading ? 'true' : undefined}
                        type={asChild ? undefined : 'button'}
                        {...iconRest}
                        {...(iconRest.autoFocus ? { 'data-auto-focus': 'true' } : {})}
                        {...(asChild && (disabled || isLoading) ? { tabIndex: -1 } : {})}
                    >
                        {isLoading ? (
                            asChild ? (
                                [<LoadingSpinner key="icon" />, <Slottable key="s">{iconChildren}</Slottable>]
                            ) : (
                                <LoadingSpinner />
                            )
                        ) : asChild ? (
                            [<Fragment key="icon">{icon}</Fragment>, <Slottable key="s">{iconChildren}</Slottable>]
                        ) : (
                            icon
                        )}
                    </Comp>
                </Tooltip>
            </SlotProvider>
        )
    }

    const { children, prefix, suffix, tooltip, label, ...textRest } = rest as Omit<
        TextButtonProps,
        'variant' | 'color' | 'size' | 'disabled' | 'isLoading' | 'asChild'
    >

    // 12px sits intentionally between x-small (8) and small (16) per the Figma spec —
    // no matching ds-tokens spacing token exists, so the arbitrary value is the documented escape.
    const textClasses = cn(
        classes,
        prefix && (size === 'small' ? 'pl-xx-small' : 'pl-[12px]'),
        suffix && (size === 'small' ? 'pr-xx-small' : 'pr-[12px]'),
    )

    const button = (
        <Comp
            ref={ref}
            className={textClasses}
            disabled={asChild ? undefined : disabled || isLoading}
            aria-disabled={disabled || isLoading ? 'true' : undefined}
            aria-label={label}
            type={asChild ? undefined : 'button'}
            {...textRest}
            {...(textRest.autoFocus ? { 'data-auto-focus': 'true' } : {})}
            {...(asChild && (disabled || isLoading) ? { tabIndex: -1 } : {})}
        >
            {isLoading ? (
                asChild ? (
                    [<LoadingSpinner key="prefix" />, <Slottable key="s">{children}</Slottable>]
                ) : (
                    <LoadingSpinner />
                )
            ) : (
                [
                    prefix === undefined ? null : <Fragment key="prefix">{prefix}</Fragment>,
                    <Slottable key="s">{children}</Slottable>,
                    suffix === undefined ? null : <Fragment key="suffix">{suffix}</Fragment>,
                ]
            )}
        </Comp>
    )

    return (
        <SlotProvider slots={{ icon: { size: textButtonIconSizeMap[size ?? 'medium'] } }}>
            {tooltip ? <Tooltip content={tooltip}>{button}</Tooltip> : button}
        </SlotProvider>
    )
})

Button.displayName = 'Button'

/* oxlint-disable @adverity/oxlint/consistent-barrels */
export { Button }
export { buttonVariants } from './button-variants'
export type { ButtonProps, TextButtonProps, IconButtonProps, ButtonBaseProps }
export type { ButtonVariant, ButtonColor, ButtonSize } from './button-variants'
/* oxlint-enable @adverity/oxlint/consistent-barrels */
